import type { CollectionBeforeValidateHook } from 'payload'
import slugify from 'slugify'

import { isNonEmptyString, toNullableString } from './strings'

export const resolveSlugLocale = (value: unknown, fallbackLocale = 'vi'): string => {
  const normalized = toNullableString(value)?.toLowerCase()

  if (!normalized) {
    return fallbackLocale
  }

  if (normalized.startsWith('vi')) {
    return 'vi'
  }

  if (normalized.startsWith('ja')) {
    return 'ja'
  }

  if (normalized.startsWith('en')) {
    return 'en'
  }

  return fallbackLocale
}

export const formatSlug = (value: unknown, locale = 'vi'): string => {
  const normalized = toNullableString(value)

  if (!normalized) {
    return ''
  }

  return slugify(normalized, {
    lower: true,
    strict: true,
    locale,
    trim: true,
  })
}

const generateRandomSegment = (length: number): string => {
  const byteLength = Math.max(1, Math.ceil(length / 2))

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(byteLength)
    globalThis.crypto.getRandomValues(bytes)
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, length)
  }

  return Array.from({ length: byteLength }, () => {
    const byte = Math.floor(Math.random() * 256)
    return byte.toString(16).padStart(2, '0')
  }).join('').slice(0, length)
}

const buildRandomSlug = (value: unknown, randomLength: number, locale: string): string => {
  const base = formatSlug(value, locale)
  const suffix = generateRandomSegment(randomLength)

  if (!base) {
    return suffix
  }

  return `${base}-${suffix}`
}

type SlugRecord = {
  slug?: string | null
  [key: string]: unknown
}

export const createSlugHook = (sourceField: string): CollectionBeforeValidateHook => {
  return ({ data, originalDoc, operation }) => {
    const workingData = data || {}
    const workingRecord = workingData as SlugRecord
    const originalRecord = (originalDoc as SlugRecord | undefined) ?? {}

    const candidateSource = workingRecord[sourceField] ?? originalRecord[sourceField]
    const sourceValue = isNonEmptyString(candidateSource) ? candidateSource : undefined

    const hasSlug = isNonEmptyString(workingRecord.slug)

    if (operation === 'create') {
      if (!hasSlug && sourceValue) {
        workingRecord.slug = formatSlug(sourceValue)
      }
    }

    if (operation === 'update') {
      const originalSlug = isNonEmptyString(originalRecord.slug) ? originalRecord.slug : undefined

      if (originalSlug) {
        workingRecord.slug = originalSlug
      } else if (!hasSlug && sourceValue) {
        workingRecord.slug = formatSlug(sourceValue)
      }
    }

    return workingData
  }
}

type RandomSlugOptions = {
  randomLength?: number
  locale?: string
  localeField?: string
  defaultLocale?: string
}

export const createRandomizedSlugHook = (
  sourceField: string,
  options?: RandomSlugOptions,
): CollectionBeforeValidateHook => {
  const randomLength = Math.max(6, options?.randomLength ?? 12)

  return ({ data, originalDoc, operation }) => {
    const workingData = data || {}
    const workingRecord = workingData as SlugRecord
    const originalRecord = (originalDoc as SlugRecord | undefined) ?? {}

    const candidateSource = workingRecord[sourceField] ?? originalRecord[sourceField]
    const sourceValue = isNonEmptyString(candidateSource) ? candidateSource : undefined
    const localeCandidate =
      options?.locale ??
      (options?.localeField
        ? workingRecord[options.localeField] ?? originalRecord[options.localeField]
        : undefined)
    const slugLocale = resolveSlugLocale(localeCandidate, options?.defaultLocale ?? 'vi')

    const hasSlug = isNonEmptyString(workingRecord.slug)
    const isPublished = (workingRecord._status ?? originalRecord._status) === 'published'

    if (operation === 'create') {
      if (!hasSlug) {
        workingRecord.slug = buildRandomSlug(
          sourceValue ?? workingRecord.slug,
          randomLength,
          slugLocale,
        )
      }
      // If user provided a slug on create, keep it as-is (they generated it via the button)
    }

    if (operation === 'update') {
      const originalSlug = isNonEmptyString(originalRecord.slug) ? originalRecord.slug : undefined
      const wasPublished = originalRecord._status === 'published'

      // Once published, slug is immutable
      if (wasPublished && originalSlug) {
        workingRecord.slug = originalSlug
      } else if (!hasSlug && !isPublished) {
        // Only auto-generate if no slug provided and still draft
        workingRecord.slug = buildRandomSlug(
          sourceValue ?? workingRecord.slug,
          randomLength,
          slugLocale,
        )
      }
      // Otherwise keep the user-provided slug (allow edits while draft)
    }

    return workingData
  }
}

type ValidateArgs = {
  operation?: string
  previousValue?: { slug?: string | null; _status?: string | null } | null
  data?: { _status?: string | null }
}

export const validateImmutableSlug = (value: unknown, args: ValidateArgs) => {
  const { operation, previousValue } = args

  if (!isNonEmptyString(value)) {
    return 'Slug is required.'
  }

  const wasPublished = previousValue?._status === 'published'
  const previousSlug = previousValue?.slug

  // Only prevent slug changes if the post was previously published
  if (
    operation === 'update' &&
    wasPublished &&
    isNonEmptyString(previousSlug) &&
    value !== previousSlug
  ) {
    return 'Slug cannot be changed after publishing.'
  }

  return true
}
